import React from 'react';

/**
 * Avertissements affichés quand une réponse ne couvre pas tous les comptes demandés.
 *
 * Les routes du dashboard bouclent sur les comptes et poursuivent en cas d'échec :
 * la réponse reste un « 200 success: true ». Sans ces avertissements, des totaux
 * amputés d'un compte s'affichent exactement comme des totaux complets.
 *
 * Deux niveaux : un bandeau en tête de dashboard, qui dit quels comptes manquent et
 * dans quelles sections, et une mention par composant, au plus près du chiffre faux.
 */

const REASON_LABELS = {
  source_introuvable: 'compte introuvable',
  connexion_absente: 'connexion Google absente ou expirée',
  customer_id_absent: 'identifiant client manquant'
};

/**
 * Les causes reconnues sous un message technique.
 *
 * Les routes du dashboard ne rendent pas toutes un code : la plupart remontent le message
 * d'erreur brut de la régie, préfixé par la section qui l'a rencontré. Le bandeau affichait
 * donc, pour un même compte, trois phrases de deux lignes disant la même chose — « Erreur
 * métriques Google Ads: [ACCESS_REVOKED] Accès au compte … révoqué », « Erreur données
 * appareils: [ACCESS_REVOKED] … » —, parce que le dédoublonnage porte sur des chaînes qui
 * diffèrent d'un préfixe.
 *
 * Reconnaître la cause **dans** le message ramène les trois à une, et la dit en français.
 * L'ordre compte : une révocation contient aussi « permission », et serait sinon rangée sous
 * la cause la plus vague.
 */
const CAUSES_CONNUES = [
  { motif: /ACCESS_REVOKED/i, libelle: 'accès révoqué', revoque: true },
  { motif: /PERMISSION_DENIED|does not have permission/i, libelle: 'accès refusé par la régie' },
  { motif: /RESOURCE_EXHAUSTED|RATE_EXCEEDED|quota/i, libelle: "quota d'API épuisé" },
  { motif: /AUTH_EXPIRED|UNAUTHENTICATED|token/i, libelle: 'connexion expirée' },
  { motif: /NOT_FOUND|introuvable/i, libelle: 'compte introuvable' },
  { motif: /timeout|délai/i, libelle: 'délai dépassé' }
];

const causeConnue = (reason) => CAUSES_CONNUES.find(c => c.motif.test(String(reason || '')));

/**
 * Un message d'erreur inconnu reste affiché, mais **écourté** : une phrase de trois lignes dans
 * une puce de bandeau se lit moins bien qu'un extrait suivi de points de suspension, et le
 * détail complet n'a jamais aidé personne à cet endroit.
 */
const describeReason = (reason) => {
  const connue = causeConnue(reason);
  if (connue) return connue.libelle;
  if (REASON_LABELS[reason]) return REASON_LABELS[reason];
  const texte = String(reason || '').trim();
  if (!texte) return 'erreur inconnue';
  return texte.length > 120 ? `${texte.slice(0, 120)}…` : texte;
};

const AMBER = { border: '#f59e0b', background: '#fffbeb', text: '#92400e' };

/** Regroupe les échecs par compte : un même compte peut manquer dans plusieurs sections. */
const groupByAccount = (blocks) => {
  const byAccount = new Map();
  blocks.forEach(({ label, failedSources }) => {
    (failedSources || []).forEach(({ sourceId, reason }) => {
      if (!byAccount.has(sourceId)) byAccount.set(sourceId, { sourceId, reasons: new Set(), sections: new Set() });
      const entry = byAccount.get(sourceId);
      entry.reasons.add(describeReason(reason));
      entry.sections.add(label);
    });
  });
  return [...byAccount.values()];
};

/**
 * Bandeau de tête. `blocks` : [{ label, failedSources }] pour chaque section chargée.
 * `resolveName` traduit un identifiant de compte en nom lisible.
 */
export const PartialDataBanner = ({ blocks = [], resolveName }) => {
  const accounts = groupByAccount(blocks.filter(b => b.failedSources?.length));
  if (accounts.length === 0) return null;

  const plural = accounts.length > 1;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        border: `1px solid ${AMBER.border}`,
        background: AMBER.background,
        color: AMBER.text,
        borderRadius: '8px',
        padding: '12px 16px',
        margin: '0 0 16px 0',
        fontSize: '0.875rem',
        lineHeight: 1.5
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '1.1rem', lineHeight: 1.3 }}>⚠️</span>
      <div>
        <strong>
          {plural
            ? `${accounts.length} comptes n'ont pas répondu : les totaux ci-dessous sont incomplets.`
            : "Un compte n'a pas répondu : les totaux ci-dessous sont incomplets."}
        </strong>
        <ul style={{ margin: '6px 0 0 0', padding: '0 0 0 18px' }}>
          {accounts.map(({ sourceId, reasons, sections }) => (
            <li key={sourceId} style={{ marginBottom: '2px' }}>
              <strong>{resolveName?.(sourceId) || sourceId}</strong>
              {' — '}{[...reasons].join(', ')}
              <span style={{ opacity: 0.8 }}>
                {' ('}{[...sections].join(', ')}{')'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/**
 * Mention discrète, à placer au plus près du tableau concerné.
 *
 * ⚠️ **Un compte absent est nommé AVEC sa cause.** Cette mention ne rendait que des noms :
 * « Données incomplètes : 1 compte absent (BZB FR - CLOSED 02.09.2026) ». Le `reason` voyage
 * pourtant jusqu'ici — tous les appelants le posent, et le bandeau de tête le montre déjà — et
 * il était jeté. Il ne restait, pour expliquer l'absence, que le **nom** du compte : celui-là
 * portait « CLOSED » dans son libellé, et l'avertissement s'est lu « Kokpit a pris ce compte
 * pour fermé » alors que son jeton Awin était refusé. Une absence sans cause se fait expliquer
 * par ce qui traîne à côté d'elle, et le nom d'un compte n'est jamais un diagnostic.
 */
export const PartialDataNotice = ({ failedSources = [], resolveName }) => {
  if (!failedSources.length) return null;

  // Regroupé par compte, comme le bandeau : un même compte peut échouer deux fois dans la
  // même réponse, et le compter deux fois annoncerait « 2 comptes absents » pour un seul.
  const causesParCompte = new Map();
  failedSources.forEach(({ sourceId, reason }) => {
    if (!causesParCompte.has(sourceId)) causesParCompte.set(sourceId, new Set());
    causesParCompte.get(sourceId).add(describeReason(reason));
  });
  const mentions = [...causesParCompte.entries()].map(([sourceId, causes]) =>
    `${resolveName?.(sourceId) || sourceId} — ${[...causes].join(', ')}`);

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        color: AMBER.text,
        background: AMBER.background,
        border: `1px solid ${AMBER.border}`,
        borderRadius: '6px',
        padding: '6px 10px',
        margin: '0 0 8px 0',
        fontSize: '0.8rem'
      }}
    >
      <span aria-hidden="true">⚠️</span>
      <span>
        Données incomplètes : {mentions.length > 1 ? `${mentions.length} comptes absents` : '1 compte absent'}
        {' ('}{mentions.join(' · ')}{')'}
      </span>
    </div>
  );
};

export default PartialDataNotice;
